/**
 * Согласия человека: что он подтвердил, какой версии и когда.
 *
 * Согласие — событие, а не галочка в настройках. Поэтому хранится запись: что
 * именно подтверждено, версия документа на тот момент, точное время и способ.
 * Без версии запись бесполезна: документ меняется, и «согласен» без указания
 * с чем именно не значит ничего.
 *
 * Два правила, которые здесь заданы структурой, а не дисциплиной:
 *
 * · **Необязательное не бывает отмечено заранее.** Согласие на рекламу и на
 *   что угодно сверх необходимого начинается с `granted: false` и становится
 *   истинным только от действия человека.
 * · **Отзыв — такая же запись.** Он не стирает прошлое согласие, а добавляет
 *   новое событие: было согласие, стало несогласие, и обе даты видны.
 *
 * Итоговые тексты документов готовятся отдельно, с юристом. Приложение хранит
 * версии и факты подтверждения; выдумывать за юриста содержание нельзя.
 */

/** Что именно подтверждается. */
export type ConsentKind =
  /** Условия использования. */
  | 'terms'
  /** Обработка персональных данных. */
  | 'privacy'
  /** Передача данных в модель для разбора — отдельное согласие. */
  | 'ai_processing'
  /** Рекламные сообщения. Необязательное, по умолчанию не дано. */
  | 'marketing';

/** Как человек это подтвердил. */
export type ConsentMethod = 'checkbox' | 'button';

export interface Consent {
  id: string;
  kind: ConsentKind;
  /** Версия документа на момент подтверждения. */
  documentVersion: string;
  /** Точное время, ISO. */
  acceptedAt: string;
  method: ConsentMethod;
  /** Дано или отозвано. Отзыв — новая запись, а не удаление старой. */
  granted: boolean;
}

/** Согласия, без которых приложением пользоваться нельзя. */
export const REQUIRED_CONSENTS: ConsentKind[] = ['terms', 'privacy'];

/**
 * Действующее состояние согласия: побеждает последняя по времени запись.
 *
 * Версия учитывается: подтверждение версии 1 не считается согласием с
 * версией 2. Молча засчитать его значило бы получить согласие на то, чего
 * человек не видел.
 */
export function consentState(
  consents: readonly Consent[], kind: ConsentKind, version: string,
): { granted: boolean; at: string | null; method: ConsentMethod | null } {
  const mine = consents
    .filter((c) => c.kind === kind && c.documentVersion === version)
    .sort((a, b) => (a.acceptedAt < b.acceptedAt ? -1 : 1));
  const last = mine[mine.length - 1];
  return last
    ? { granted: last.granted, at: last.acceptedAt, method: last.method }
    : { granted: false, at: null, method: null };
}

/** Каких обязательных согласий этой версии не хватает. */
export const missingConsents = (
  consents: readonly Consent[], version: string,
): ConsentKind[] => REQUIRED_CONSENTS.filter((k) => !consentState(consents, k, version).granted);
